import React, { useEffect, useState, Fragment } from 'react';
import { Box, List, ListItem, ListItemText, ListSubheader, ListItemIcon, Divider, Typography, Collapse } from '@material-ui/core';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Terrain, Description, Waves, EmojiNature } from '@material-ui/icons';
import { useHistory, useLocation, useRouteMatch } from 'react-router';

import { DrawerWrapper } from 'src/components';
import { urlJoin, truncateWithEllipsis as twe } from 'src/utils/util';
import {
  MapSearch, MonitorDashboard, VectorUnion, Water, Graph, Speedometer, FormatListBulletedType, TimeLine, Cash, Calculator, BullseyeArrow,
  ListStatus,
} from 'src/utils/mdIcons';


const useStyles = makeStyles(theme => ({
  titleBox: {
    height: '64px',
    textOverflow: 'ellipsis',
  },
  title: {
    color: theme.palette.common.gray800,
    lineHeight: 1.25,
    fontWeight: 500,
  },
  overTitle: {
    lineHeight: 1,
  },
  listItemSelected: {
    backgroundColor: `${theme.palette.secondary.main} !important`,
    color: theme.palette.common.white,
  },
  nested: {
    paddingLeft: theme.spacing(4),
  },
  listItem: {
    borderRadius: '6px',
  },
  list: {
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
  listItemIcon: {
    minWidth: '36px',
  },
  sectionHeader: {
    lineHeight: 0,
  },
  paperRoot: {
    marginLeft: '97px',
  },
}));

const itemList = [
  {
    Icon: MonitorDashboard,
    label: 'Visión general',
    endPath: 'pressures-and-dependencies',
    sectionName: 'Impactos y dependencias',
    isHiddenForSites: true,
  },
  {
    Icon: Speedometer,
    label: 'Presiones',
    endPath: 'pressures',
    hasToCheckIsic: true,
    isHiddenForSites: true,
  },
  {
    Icon: Graph,
    label: 'Dependencias',
    endPath: 'dependencies',
    hasToCheckIsic: true,
    isHiddenForSites: true,
  },
  {
    Icon: MonitorDashboard,
    label: 'Visión general',
    endPath: 'overview',
    sectionName: 'Estado de la naturaleza',
    isHiddenForSites: true,
  },
  {
    Icon: Terrain,
    label: 'Tierra',
    endPath: 'land',
    hasToCheckInterestArea: true,
    isHiddenForSites: true,
  },
  {
    Icon: Terrain,
    label: 'Tierra',
    endPath: 'land',
    sectionName: 'Componentes',
    hasToCheckInterestArea: true,
    isHiddenForProjects: true,
  },
  {
    Icon: EmojiNature,
    label: 'Biodiversidad',
    endPath: 'biodiversity',
    hasToCheckInterestArea: true,
  },
  {
    Icon: Water,
    label: 'Agua dulce',
    endPath: 'freshwater',
    hasToCheckInterestArea: true,
    isDisabled: false,
  },
  {
    Icon: Waves,
    label: 'Oceano',
    endPath: 'ocean',
    hasToCheckInterestArea: true,
    isDisabled: true,
  },
  {
    Icon: TimeLine,
    label: 'Monitoreos SMA',
    endPath: 'monitoring-plans',
    subPaths: [ 'comparison', 'analytics', 'groups' ],
    isHiddenForProjects: true,
  },
  {
    Icon: MonitorDashboard,
    label: 'Visión general',
    endPath: 'sites-overview',
    sectionName: 'Sitios',
    isHiddenForSites: true,
  },
  {
    Icon: MapSearch,
    label: 'Sitios',
    endPath: 'sites',
    isHiddenForSites: true,
  },
  {
    Icon: Calculator,
    label: 'Contabilidad física',
    endPath: 'physical-accounting',
    sectionName: 'Capital natural',
    isHiddenForSites: true,
  },
  {
    Icon: Cash,
    label: 'Contabilidad monetaria',
    endPath: 'monetary-accounting',
    isDisabled: true,
    isHiddenForSites: true,
  },
  {
    Icon: BullseyeArrow,
    label: 'Metas',
    endPath: 'targets',
    sectionName: 'Metas',
    isDisabled: true,
    isHiddenForSites: true,
  },
  {
    Icon: VectorUnion,
    label: 'Áreas de interés',
    endPath: 'interest-areas',
    sectionName: 'Gestión de datos',
    emailDomainsAllowed: [ 'nviro.cl', 'csw.cl' ],
  },
  {
    Icon: ListStatus,
    label: 'Estado de avance',
    endPath: 'progress-status',
    emailDomainsAllowed: [ 'nviro.cl', 'csw.cl' ],
  },
  {
    Icon: FormatListBulletedType,
    label: 'Clasificación ISIC',
    endPath: 'isic',
    isHiddenForSites: true,
    emailDomainsAllowed: [ 'nviro.cl', 'csw.cl' ],
  },
  {
    Icon: TimeLine,
    label: 'Monitoreos SMA',
    endPath: 'monitoring-plans',
    subPaths: [ 'comparison', 'analytics', 'groups' ],
    isHiddenForSites: true,
    emailDomainsAllowed: [ 'nviro.cl', 'csw.cl' ],
  },
  {
    Icon: Description,
    label: 'Fuentes de datos',
    endPath: 'import',
    subItems: [
      {
        Icon: () => <Description fontSize="small" />,
        label: 'SMA DarwinCore',
        endPath: 'sma',
        enabled: true,
      },
      {
        Icon: () => <Description fontSize="small" />,
        label: 'Nviro Capture',
        endPath: 'capture',
        enabled: true,
      },
    ],
    isCollapsable: false,
    subPaths: [ 'sma', 'capture' ],
    allowOnly: [ 'admin' ],
    isHiddenForSites: true,
    emailDomainsAllowed: [ 'nviro.cl', 'csw.cl' ],
  },
];

const getIndex = (filteredItemList, currentPathname) => {
  const lastString = currentPathname.split('/').pop();
  const index = filteredItemList.findIndex(item => item.endPath === lastString);
  let idx;
  if (index === -1) {
    filteredItemList.forEach((item, index) => {
      if (item.subItems) {
        const subIndex = item.subItems.findIndex(si => si.endPath === lastString);
        if (subIndex !== -1) {
          idx = `${index}-${subIndex}`;
        }
      }
    });
  } else {
    idx = index;
  }
  return idx;
};

const ProjectDrawer = ({ name, userRoleId, hasInterestAreas, hasIsic, siteName, userEmail }) => {
  const classes = useStyles();
  const history = useHistory();
  const match = useRouteMatch();
  const { pathname } = useLocation();
  const startIndex = getIndex(itemList, pathname);
  const emailDomain = userEmail?.split('@')[1];

  const [ selectedIndex, setSelectedIndex ] = useState(startIndex);

  const [ open, setOpen ] = useState(startIndex && typeof startIndex !== 'number' ? { [startIndex.split('-')[0]]: true } : {});

  const { projectId, siteId } = match.params;
  const isSite = Boolean(siteId);

  const baseUrl = `/web/projects/${projectId}/`;

  const filteredItemList = itemList.filter(({ allowOnly, isHiddenForSites, isHiddenForProjects, emailDomainsAllowed }) => {
    if (isSite && isHiddenForSites) {
      return false;
    }
    if (!isSite && isHiddenForProjects) {
      return false;
    }
    if (allowOnly && !allowOnly.includes(userRoleId)) {
      return false;
    }
    if (emailDomainsAllowed && !emailDomainsAllowed.includes(emailDomain)) {
      return false;
    }
    return true;
  });

  const handleSelectedIndex = index => () => {
    if (filteredItemList[index]?.disabled) {
      return;
    }
    if (filteredItemList[index]?.subItems) {
      setOpen(prev => ({ ...prev, [index]: !prev[index] }));
    } else {
      let targetUrl;
      if (isSite) {
        if (typeof index === 'number') {
          targetUrl = urlJoin('/web/projects', projectId, 'sites', siteId, filteredItemList[index].endPath);
        } else {
          const [ str1, str2 ] = index.split('-');
          targetUrl = urlJoin(
            '/web/projects',
            projectId,
            'sites',
            siteId,
            filteredItemList[str1].endPath,
            filteredItemList[str1].subItems[str2].endPath,
          );
          setSelectedIndex({ index });
        }
      } else {
        if (typeof index === 'number') {
          targetUrl = urlJoin(baseUrl, filteredItemList[index].endPath);
        } else {
          const [ str1, str2 ] = index.split('-');
          targetUrl = urlJoin(baseUrl, filteredItemList[str1].endPath, filteredItemList[str1].subItems[str2].endPath);
          setSelectedIndex({ index });
        }
      }
      history.push(targetUrl);
    }
  };

  useEffect(() => {
    setSelectedIndex(getIndex(filteredItemList, pathname));
  }, [ filteredItemList.length, pathname ]); // eslint-disable-line

  const commonListItemProps = { button: true, component: 'a', classes: { root: classes.listItem, selected: classes.listItemSelected } };

  return (
    <DrawerWrapper anchor="left" PaperProps={{ classes: { root: classes.paperRoot } }}>
      <Box mt={7}>
        <Box my={5} />
        <Box ml={2} display="flex" alignItems="center">
          <Box px={1} display="flex" flexDirection="column" justifyContent="center" className={classes.titleBox}>
            <Typography variant="caption" className={classes.overTitle}>{isSite ? 'Sitio' : 'Proyecto'}</Typography>
            <Typography className={classes.title} variant="body1">{twe(isSite ? siteName : name, 70)}</Typography>
          </Box>
        </Box>
        <List className={classes.list}>
          {filteredItemList.map(
            (
              {
                Icon,
                label,
                subItems,
                sectionName,
                isCollapsable,
                hasToCheckInterestArea = false,
                hasToCheckIsic = false,
                isDisabled = false,
              }, itemIndex,
            ) => (
              <Fragment key={itemIndex}>
                {(() => {
                  const prevItem = filteredItemList[itemIndex - 1];
                  // Solo ocultar el sectionName si es un sitio y el anterior es hidden y tiene sectionName
                  const prevIsHiddenSection = isSite && prevItem?.isHiddenForSites && prevItem?.sectionName;
                  return sectionName && !prevIsHiddenSection;
                })() &&
                  <>
                    <Box {...(itemIndex === 0 ? { pb: 2 } : { py: 1 })} mx={-1}>
                      <Divider />
                    </Box>
                    <Box py={2}>
                      <ListSubheader className={classes.sectionHeader} color="primary">
                        {sectionName}
                      </ListSubheader>
                    </Box>
                  </>
                }
                <ListItem
                  disabled={(hasToCheckInterestArea && !hasInterestAreas) || (hasToCheckIsic && !hasIsic) || isDisabled}
                  selected={selectedIndex === itemIndex}
                  onClick={handleSelectedIndex(itemIndex)}
                  {...commonListItemProps}
                >
                  <ListItemIcon classes={{ root: classes.listItemIcon }}>
                    <Icon />
                  </ListItemIcon>
                  <ListItemText primary={label} />
                </ListItem>
                {subItems &&
                    <Collapse in={isCollapsable ? open[itemIndex] : true} timeout="auto" unmountOnExit>
                      <List className={classes.list} disablePadding>
                        {subItems.map(({ Icon, label }, subitemIndex) =>
                          <ListItem key={`${itemIndex}-${subitemIndex}`} selected={selectedIndex === `${itemIndex}-${subitemIndex}`}
                            onClick={handleSelectedIndex(`${itemIndex}-${subitemIndex}`)} button
                            className={classes.nested} {...commonListItemProps}>
                            <ListItemIcon classes={{ root: classes.listItemIcon }}>
                              <Icon />
                            </ListItemIcon>
                            <ListItemText primary={label} />
                          </ListItem>,
                        )}
                      </List>
                    </Collapse>
                }
              </Fragment>
            ))}
        </List>
      </Box>
    </DrawerWrapper>
  );
};

ProjectDrawer.propTypes = {
  name: PropTypes.string,
  siteName: PropTypes.string,
  userEmail: PropTypes.string,
  userRoleId: PropTypes.oneOf([ 'admin', 'user', 'consultant' ]),
  hasInterestAreas: PropTypes.bool,
  hasIsic: PropTypes.bool,
};


export { ProjectDrawer };
